<script setup>
import { computed, shallowRef } from 'vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import NotificationFeed from './NotificationFeed.vue';
import { useNotifications } from '@/composables/useNotifications';

// Header bell: unread count on the icon, a popover with the latest five and
// "View all" opening the full list in a dialog (the popover closes first,
// so only one overlay is ever open).
const props = defineProps({
  userId: { type: [Number, String], default: null },
  roleCode: { type: String, default: null }, // active role, e.g. "teacher"
  basePath: { type: String, required: true }, // e.g. "/employee"
});

const PREVIEW = 5;

const { items, tabs, unreadCount, open, markAllRead } = useNotifications({
  userId: () => props.userId,
  roleCode: () => props.roleCode,
  basePath: () => props.basePath,
});

const menuOpen = shallowRef(false);
const showAll = shallowRef(false);
const tab = shallowRef('');

const countLabel = computed(() => (unreadCount.value > 99 ? '99+' : String(unreadCount.value)));
const summary = computed(() =>
  unreadCount.value ? `${unreadCount.value} unread` : "You're all caught up",
);

function onOpen(item) {
  menuOpen.value = false;
  showAll.value = false;
  open(item);
}
function viewAll() {
  menuOpen.value = false;
  showAll.value = true;
}
</script>

<template>
  <v-menu
    v-model="menuOpen"
    location="bottom end"
    offset="8"
    :close-on-content-click="false"
  >
    <template #activator="{ props: menuProps }">
      <button
        v-bind="menuProps"
        type="button"
        class="bell"
        :class="{ 'bell--open': menuOpen }"
        :aria-label="unreadCount ? `Notifications, ${unreadCount} unread` : 'Notifications'"
      >
        <v-icon size="18">mdi-bell-outline</v-icon>
        <span v-if="unreadCount" class="bell__count">{{ countLabel }}</span>
      </button>
    </template>

    <div class="panel">
      <header class="panel__head">
        <div>
          <h3 class="panel__title">Notifications</h3>
          <p class="panel__summary">{{ summary }}</p>
        </div>
      </header>

      <div class="panel__body">
        <NotificationFeed
          v-model:tab="tab"
          :items="items"
          :tabs="tabs"
          :limit="PREVIEW"
          compact
          @open="onOpen"
        />
      </div>

      <footer v-if="items.length" class="panel__foot">
        <button v-if="unreadCount" type="button" class="panel__mark" @click="markAllRead">
          <v-icon size="16">mdi-check-all</v-icon>
          Mark all as read
        </button>
        <button type="button" class="panel__all" @click="viewAll">
          View all notifications
          <v-icon size="16">mdi-arrow-right</v-icon>
        </button>
      </footer>
    </div>
  </v-menu>

  <UiDialog v-model="showAll" title="Notifications" :description="summary" size="lg">
    <NotificationFeed v-model:tab="tab" :items="items" :tabs="tabs" @open="onOpen" />
    <template #footer="{ close }">
      <v-btn v-if="unreadCount" variant="text" prepend-icon="mdi-check-all" class="mr-auto" @click="markAllRead">
        Mark all as read
      </v-btn>
      <v-btn variant="outlined" @click="close">Close</v-btn>
    </template>
  </UiDialog>
</template>

<style scoped>
.bell {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--foreground);
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.bell:hover,
.bell--open {
  background: var(--accent);
}
.bell:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(var(--brand-rgb), 0.3);
}
.bell__count {
  position: absolute;
  top: 2px;
  right: 0;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border: 2px solid var(--background);
  border-radius: 999px;
  background: var(--brand);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  line-height: 14px;
  text-align: center;
  box-sizing: border-box;
}

.panel {
  display: flex;
  flex-direction: column;
  width: 380px;
  max-width: calc(100vw - 16px);
  max-height: min(600px, calc(100vh - 96px));
  overflow: hidden;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
  color: var(--foreground);
}
.panel__head {
  display: flex;
  align-items: center;
  padding: 14px 16px 10px;
}
.panel__title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}
.panel__summary {
  margin: 2px 0 0;
  font-size: 13px;
  color: var(--muted-foreground);
}
.panel__body {
  min-height: 0;
  overflow-y: auto;
  padding: 0 8px 8px;
}
.panel__foot {
  display: flex;
  gap: 4px;
  border-top: 1px solid var(--border);
  padding: 4px;
}
.panel__all,
.panel__mark {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 1;
  height: 36px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--foreground);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}
.panel__mark {
  color: var(--muted-foreground);
}
.panel__all:hover,
.panel__mark:hover {
  background: var(--accent);
  color: var(--foreground);
}
.panel__all:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(var(--brand-rgb), 0.3);
}
</style>
